// ─────────────────────────────────────────────────────────────────────────────
// JOVINO COIN (JCV) — CENTRAL CONFIGURATION & DATA LAYER
// Update project information here. The presale status, whitepaper versions,
// social links and ecosystem statuses can also be overridden at runtime via
// the backend /api/config endpoint (admin-controlled, stored in MongoDB).
// ─────────────────────────────────────────────────────────────────────────────

export const JOVINO = {
  token: {
    name: "Jovino Coin",
    ticker: "JCV",
    blockchain: "BNB Smart Chain",
    standard: "BEP-20",
    decimals: 18,
    maxSupply: "72,000,000,000,000",
    maxSupplyRaw: 72000000000000,
    inflation: "None",
    minting: "Permanently Disabled",
    burning: "Community-approved buyback & burn",
    governance: "DAO — Future Phase",
    contractAddress: null, // TO BE ANNOUNCED — never fabricate
  },

  presale: {
    // Admin-controlled status: "upcoming" | "live" | "ended" | "sold_out"
    // Do NOT auto-switch based on the calendar — configuration decides.
    status: "upcoming",
    startDateISO: "2026-11-01T00:00:00Z",
    displayDate: "1 November 2026",
    // Reserved for White Paper v2.0 — null means "not announced".
    price: null,
    hardCap: null,
    softCap: null,
    allocation: null,
    paymentCurrency: null,
    minPurchase: null,
    maxPurchase: null,
    detailsLabel: "Presale Details Coming Soon",
  },

  nav: [
    { label: "Home", href: "#home" },
    { label: "About", href: "#about" },
    { label: "Tokenomics", href: "#tokenomics" },
    { label: "Utility", href: "#utility" },
    { label: "Ecosystem", href: "#ecosystem" },
    { label: "Roadmap", href: "#roadmap" },
    { label: "Governance", href: "#governance" },
    { label: "Security", href: "#security" },
    { label: "Whitepaper", href: "#whitepaper" },
    { label: "FAQ", href: "#faq" },
  ],

  badges: ["BEP-20", "BNB SMART CHAIN", "NO FUTURE MINTING"],

  problems: [
    {
      title: "Excessive Speculation",
      body: "Many digital assets derive value primarily from speculation rather than genuine utility.",
    },
    {
      title: "Inflationary Token Models",
      body: "Continuous token issuance can dilute existing holders. JCV uses a permanently fixed maximum supply.",
    },
    {
      title: "High Transaction Costs",
      body: "JCV leverages BNB Smart Chain infrastructure for comparatively low transaction costs and fast confirmations.",
    },
    {
      title: "Limited Governance",
      body: "Jovino Coin is designed to progressively transition toward decentralized community governance through a DAO.",
    },
  ],

  specifications: [
    ["Name", "Jovino Coin"],
    ["Symbol", "JCV"],
    ["Blockchain", "BNB Smart Chain"],
    ["Standard", "BEP-20"],
    ["Decimals", "18"],
    ["Maximum Supply", "72,000,000,000,000 JCV"],
    ["Inflation", "None"],
    ["Minting", "Permanently Disabled"],
    ["Burning", "Community-approved buyback & burn"],
    ["Governance", "DAO — Future Phase"],
  ],

  supplyPillars: [
    {
      title: "No Future Minting",
      body: "The mint function is permanently disabled at deployment. No entity can ever create additional JCV.",
    },
    {
      title: "No Inflation",
      body: "The maximum supply never increases. Holders are never diluted by new issuance.",
    },
    {
      title: "Predictable Supply",
      body: "A fixed, publicly verifiable supply schedule that can only decrease through community-approved burns.",
    },
  ],

  // Palette restricted to the Jovino color language (teals, golds, navy-grays).
  allocations: [
    { id: "community", name: "Community & Public Distribution", percent: 40, amount: "28,800,000,000,000", color: "#18B7A0" },
    { id: "liquidity", name: "Liquidity & Exchange Support", percent: 20, amount: "14,400,000,000,000", color: "#0F7A6A" },
    { id: "ecosystem", name: "Ecosystem Development", percent: 15, amount: "10,800,000,000,000", color: "#D6B56A" },
    { id: "staking", name: "Staking Rewards", percent: 10, amount: "7,200,000,000,000", color: "#A9884B" },
    { id: "treasury", name: "Foundation Treasury", percent: 10, amount: "7,200,000,000,000", color: "#3D6E8C" },
    { id: "team", name: "Team & Advisors", percent: 5, amount: "3,600,000,000,000", color: "#91A5B5" },
  ],

  vesting: [
    { name: "Team & Advisors", lock: "12 Months", release: "Linear over 36 Months" },
    { name: "Foundation Treasury", lock: "6 Months", release: "Governance-controlled" },
    { name: "Ecosystem Development", lock: "3 Months", release: "Milestone-based" },
    { name: "Community Allocation", lock: "None", release: "Public distribution schedule" },
    { name: "Liquidity", lock: "Immediate", release: "Controlled deployment" },
    { name: "Staking Rewards", lock: "None", release: "Distributed according to staking protocol" },
  ],

  utilities: [
    { icon: "ArrowLeftRight", title: "Network Payments", body: "Transfer value globally using BNB Smart Chain infrastructure.", status: "AT LAUNCH", tone: "teal" },
    { icon: "Storefront", title: "Merchant Payments", body: "Businesses may accept JCV through supported future integrations.", status: "FUTURE INTEGRATION", tone: "slate" },
    { icon: "LockKey", title: "Staking", body: "Holders may lock JCV and participate in the staking ecosystem.", status: "PLANNED", tone: "slate" },
    { icon: "Votes", title: "Governance", body: "Eligible holders may participate in future governance proposals.", status: "FUTURE PHASE", tone: "slate" },
    { icon: "GridFour", title: "Ecosystem Services", body: "Future Jovino applications may use JCV for premium features, APIs, or decentralized services.", status: "FUTURE", tone: "slate" },
    { icon: "Code", title: "Developer Incentives", body: "Developers contributing applications, infrastructure and tools may receive ecosystem grants denominated in JCV.", status: "PLANNED", tone: "slate" },
  ],

  staking: {
    status: "PLANNED",
    note: "Staking rewards are intended to come from the dedicated staking allocation (7,200,000,000,000 JCV) rather than newly minted tokens. No APY or APR is advertised until officially provided.",
    tiers: [
      { lock: "90 Days", multiplier: "1.00×" },
      { lock: "180 Days", multiplier: "1.20×" },
      { lock: "240 Days", multiplier: "1.50×" },
      { lock: "365 Days", multiplier: "2.00×" },
    ],
  },

  ecosystem: [
    { id: "wallet", name: "Jovino Coin Wallet", body: "Non-custodial wallet for storing, sending, receiving and staking JCV.", status: "PLANNED" },
    { id: "explorer", name: "Jovino Coin Explorer", body: "Explorer for JCV transactions, treasury transparency and governance activity.", status: "PLANNED" },
    { id: "governance", name: "Governance Portal", body: "Future proposal and voting platform.", status: "COMING IN FUTURE PHASE" },
    { id: "developer", name: "Developer Hub", body: "Documentation, APIs, SDKs and examples.", status: "PLANNED" },
    { id: "merchant", name: "Merchant Portal", body: "Tools for businesses to accept JCV payments and manage settlements.", status: "PLANNED" },
  ],

  governanceStages: [
    {
      stage: "STAGE I",
      title: "Foundation Governance",
      status: "INITIAL PHASE",
      items: ["Protocol development", "Smart contract deployment", "Treasury administration", "Exchange relationships", "Security oversight", "Regulatory engagement", "Partnership development"],
    },
    {
      stage: "STAGE II",
      title: "Community Participation",
      status: "INTERMEDIATE PHASE",
      items: ["Ecosystem grants", "Marketing initiatives", "Community events", "Educational programs", "Feature prioritization"],
    },
    {
      stage: "STAGE III",
      title: "DAO Governance",
      status: "FUTURE PHASE",
      items: ["Treasury allocations", "Protocol upgrades", "Grant approvals", "Strategic partnerships", "Ecosystem expansion"],
    },
  ],

  securityPrinciples: [
    "Minimize unnecessary smart-contract complexity",
    "Reduce privileged administrative functions",
    "Independent security audits before launch",
    "Responsible vulnerability disclosure",
    "Multi-signature treasury protection",
    "Hardware-secured private keys",
    "Operational monitoring",
    "Incident response planning",
  ],
  auditStatus: "Independent Security Audit — Planned Before Deployment",

  roadmap: [
    {
      phase: "Phase I", title: "Foundation", years: "Year 1",
      items: ["Publish official white paper", "Establish Jovino Coin Foundation", "Deploy JCV smart contract", "Complete independent security audits", "Launch official website", "Publish developer documentation", "Create community communication channels"],
    },
    {
      phase: "Phase II", title: "Market Launch", years: "Year 1–2",
      items: ["Launch JCV", "Provide initial liquidity", "List on decentralized exchanges", "Begin exchange outreach", "Release Jovino Coin Wallet", "Introduce staking"],
    },
    {
      phase: "Phase III", title: "Ecosystem Growth", years: "Year 2–3",
      items: ["Launch governance portal", "Expand merchant payment integrations", "Publish developer SDK", "Introduce ecosystem grants", "Support community-built applications", "Increase international awareness"],
    },
    {
      phase: "Phase IV", title: "Decentralization", years: "Year 3–4",
      items: ["Implement DAO governance", "Transition treasury oversight", "Expand ecosystem partnerships", "Improve interoperability", "Enhance developer tooling"],
    },
    {
      phase: "Phase V", title: "Global Expansion", years: "Year 5+",
      items: ["Mature governance processes", "Increase enterprise adoption", "Expand merchant network", "Encourage cross-chain integrations", "Continue ecosystem innovation"],
    },
  ],
  roadmapDisclaimer: "Roadmap items represent strategic objectives rather than guaranteed delivery dates. Progress is subject to technical readiness, security considerations, available resources and community priorities.",

  interoperability: {
    items: ["Cross-chain bridges", "Wrapped assets", "Decentralized interoperability protocols", "Multi-chain application support"],
    status: "FUTURE DEVELOPMENT",
  },

  vision: [
    "Decentralized identity",
    "Developer tooling and APIs",
    "Cross-chain interoperability",
    "Merchant payment gateways",
    "Tokenized digital assets",
    "Web3 integrations",
    "Enterprise blockchain services",
  ],

  whitepaper: {
    versions: [
      { version: "1.0", title: "Foundation & Protocol Overview", status: "AVAILABLE", file: "/JCV-Whitepaper-v1.0.pdf" },
      { version: "2.0", title: "Presale & Launch Economics", status: "COMING SOON", file: null },
    ],
  },

  faq: [
    { q: "What is Jovino Coin?", a: "Jovino Coin (JCV) is a decentralized utility token built on the BNB Smart Chain using the BEP-20 standard." },
    { q: "What is the maximum supply?", a: "72,000,000,000,000 JCV." },
    { q: "Can new JCV tokens be minted?", a: "No. Minting is permanently disabled after deployment." },
    { q: "What blockchain does JCV use?", a: "BNB Smart Chain." },
    { q: "What is the token standard?", a: "BEP-20." },
    { q: "What is JCV intended to be used for?", a: "Payments, merchant payments, staking, governance, ecosystem services, developer incentives and future decentralized applications." },
    { q: "When does the presale begin?", a: "1 November 2026." },
    { q: "What are the presale terms?", a: "Detailed presale terms will be announced in a future documentation release." },
    { q: "Will JCV have staking?", a: "Staking is planned and is intended to use the dedicated staking allocation rather than newly minted tokens." },
    { q: "Will JCV have DAO governance?", a: "DAO governance is planned as a future phase." },
    { q: "Is JCV audited?", a: "An independent security audit is planned before deployment. Audit documentation will be published once officially completed." },
  ],

  legal: {
    points: [
      "JCV is designed as a decentralized digital utility token.",
      "Ownership of JCV does not represent equity, debt, ownership, profit-sharing rights or a claim against the Jovino Coin Foundation or affiliated organizations.",
      "Digital assets involve significant risks. Cryptocurrency markets can be highly volatile.",
      "Smart-contract vulnerabilities may occur and cybersecurity threats exist.",
      "Regulatory requirements may change and technological failures can occur.",
      "Digital assets can be lost.",
      "Website content does not constitute financial, legal, tax, accounting, investment or regulatory advice.",
      "No future performance or financial return is guaranteed.",
      "Users should conduct independent research and seek qualified professional advice.",
    ],
  },

  // Placeholders until official URLs are supplied — never invent accounts.
  socials: [
    { name: "X (Twitter)", url: null },
    { name: "Telegram", url: null },
    { name: "Discord", url: null },
    { name: "Medium", url: null },
  ],

  marquee: [
    "FIXED SUPPLY",
    "72,000,000,000,000 JCV",
    "NO FUTURE MINTING",
    "BEP-20",
    "BNB SMART CHAIN",
    "BUILT FOR UTILITY",
    "DESIGNED FOR SCARCITY",
    "POWERED BY COMMUNITY",
  ],
};

export const PRESALE_STATUS_LABELS = {
  upcoming: "Presale Details Coming Soon",
  live: "Presale Is Live",
  ended: "Presale Has Ended",
  sold_out: "Sold Out",
};
